import { useState, useEffect } from "react";
import { Button } from "../ui/button";
import { Card, CardContent } from "../ui/card";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Label } from "../ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../ui/dialog";
import { Clock } from "lucide-react";
import { useAuth } from "../../App";

const AddTimeDialog = ({ onAddTimeEntry }) => {
  const { token, API } = useAuth();
  const [open, setOpen] = useState(false);
  const [timeEntry, setTimeEntry] = useState({ 
    hours: "", 
    description: "", 
    date: "",
    category: "other"
  });
  const [projectPhases, setProjectPhases] = useState({});

  useEffect(() => {
    fetchProjectPhases();
  }, []);

  const fetchProjectPhases = async () => {
    try {
      const response = await fetch(`${API}/admin/project-phases`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.ok) {
        const data = await response.json();
        setProjectPhases(data);
      }
    } catch (error) {
      console.error("Request failed:", error);
    }
  };

  const handleSubmit = async () => {
    const success = await onAddTimeEntry(timeEntry);
    if (success) {
      setOpen(false);
      setTimeEntry({ hours: "", description: "", date: "", category: "other" });
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Card className="cursor-pointer hover:bg-slate-50 transition-colors">
          <CardContent className="p-4 flex items-center gap-3">
            <div className="w-10 h-10 bg-rnb-blue/10 rounded-lg flex items-center justify-center">
              <Clock className="h-5 w-5 text-rnb-blue" />
            </div>
            <div>
              <p className="font-medium text-rnb-navy">Add Time</p>
              <p className="text-xs text-rnb-gray">Log hours</p>
            </div>
          </CardContent>
        </Card>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Add Time Entry</DialogTitle>
          <DialogDescription>
            Log hours spent on this client&apos;s project.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 pt-4">
          <div className="space-y-2">
            <Label>Hours</Label>
            <Input
              type="number"
              step="0.5"
              value={timeEntry.hours}
              onChange={(e) => setTimeEntry({ ...timeEntry, hours: e.target.value })}
              placeholder="e.g., 2.5"
              data-testid="time-hours"
            />
          </div>
          <div className="space-y-2">
            <Label>Category</Label>
            <Select 
              value={timeEntry.category} 
              onValueChange={(value) => setTimeEntry({ ...timeEntry, category: value })}
            >
              <SelectTrigger data-testid="time-category">
                <SelectValue placeholder="Select category" />
              </SelectTrigger>
              <SelectContent>
                {Object.entries(projectPhases).map(([key, phase]) => (
                  <SelectItem key={key} value={key}>
                    {phase.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label>Description</Label>
            <Textarea
              value={timeEntry.description}
              onChange={(e) => setTimeEntry({ ...timeEntry, description: e.target.value })}
              placeholder="What work was done?"
              data-testid="time-description"
            />
          </div>
          <div className="space-y-2">
            <Label>Date (optional)</Label>
            <Input
              type="date"
              value={timeEntry.date}
              onChange={(e) => setTimeEntry({ ...timeEntry, date: e.target.value })}
            />
          </div>
          <Button onClick={handleSubmit} className="w-full" data-testid="submit-time">
            Add Time Entry
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default AddTimeDialog;
